import React from 'react';
import { View, StyleSheet, TouchableOpacity, TextInput } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useSelector, useDispatch } from 'react-redux';

import { CommonProperties, Color, CommonStyles } from '../../styles';
import Button from '../Button';
import BaseText from '../BaseText';
import SearchTypeCheckbox from './SearchTypeCheckbox';
import SearchSubTypePicker from './SearchSubTypePicker';
import SearchDateDisplay from './SearchDateDisplay';
import SearchArrivalCityDisplay from './SearchArrivalCityDisplay';
import SearchDepartureCityDisplay from './SearchDepartureCityDisplay';
import SearchTripOptionsDisplay from './SearchTripOptionsDisplay';
import { Modals } from '../../constants/ScreenNames';
import { selectGeolocation } from '../../store/selectors/trip.selector';
import { scale } from '../../utils/scale.utils';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../../i18n';
import useTripSearchWidget from '../../utils/useTripSearchWidget';
import { EVENTS_ENABLED, AA_ENABLED } from '../../config/env';
import {
  selectTezEvent,
  selectTezEventGroup,
  selectTezEventPassengers,
} from '../../store/selectors/events.selector';
import { AAServiceTypes, searchTypesEnum, subSearchTypeEnum } from '../../constants/Enums';
import { ActionSheetDropdown } from '../ActionSheetDropdown';
import { setIsBXCurrencyCulture } from '../../store/actions/auth';
import { isBXservice } from '../../utils/search.utils';
import { useDateFormat } from '../../utils/useDateFormat';
import Messages from '../event/Messages';
import { pageKeys } from '../../constants/PageKeys';
import { selectTokenData } from '../../store/selectors/auth.selector';

const TripSearchFormFields = ({ criteria, searchInputState, index, showPicker, dispatch }) => {
  const { t } = useTranslation(i18nNS.searchWidget);
  const { tKey } = useComponentTKey('TripSearchWidget');
  const geolocation = useSelector(selectGeolocation);
  const route = useRoute();
  const navigation = useNavigation();
  const {
    hasAir,
    hasHotel,
    hasCar,
    hasRail,
    subSearchType,
    dominantSearchType,
    airClassType,
    searchCriteria,
    enableSearchTypeSelector,
    enablePassengerClass,
    tripOptions,
    serviceType,
    authNumber,
    travelersLimit,
  } = searchInputState;
  const hideDeparture = dominantSearchType === 'hotel';
  const eventData = useSelector(selectTezEvent);

  const hideArrival = dominantSearchType === 'car' && subSearchType === 'roundtrip';

  if (subSearchType !== 'multicity' && index !== 0) {
    return null;
  }
  const isLast = searchCriteria.length - 1 === index;
  function options() {
    if (eventData === undefined) {
      return [];
    }
    if (EVENTS_ENABLED && dominantSearchType === 'car') {
      const airports = eventData?.AirportCd?.split(',');
      const air = airports.map((airport) => airport);
      return air[0];
    }
    return criteria.fromCity;
  }
  const firstOption = options();

  function getDepartureCity() {
    if (!EVENTS_ENABLED && criteria.fromCity) {
      if (searchInputState.isGeoFromSearch && dominantSearchType != searchTypesEnum.CAR) {
        return '';
      } else {
        return criteria.fromCity;
      }
    } else {
      return firstOption;
    }
  }
  const arrivalToCity =
    searchInputState.isGeoToSearch &&
    dominantSearchType != searchTypesEnum.CAR &&
    dominantSearchType != searchTypesEnum.HOTEL
      ? ''
      : criteria.toCity;
  return (
    <>
      <View testID='SearchWidget' style={styles.widgetContainer}>
        {showPicker && enableSearchTypeSelector && !!dominantSearchType && (
          <SearchSubTypePicker
            dominantSearchType={dominantSearchType}
            subSearchType={subSearchType}
            rowBtnStyle={styles.rowButton}
            onSelection={(selectedSubSearchType) => {
              dispatch({ type: 'setSearchSubType', subSearchType: selectedSubSearchType });
              if (selectedSubSearchType === 'multicity' && searchCriteria.length === 1) {
                dispatch({ type: 'addCity' });
              }
            }}
          />
        )}
        {!hideDeparture && (
          <TouchableOpacity
            style={styles.rowButton}
            onPress={() =>
              navigation.navigate(Modals.StationAutoCompleteModal, {
                returnScreen: route.name,
                source: 'from',
                index,
                hasAir,
                hasHotel,
                hasCar,
                hasRail,
                subSearchType,
                dominantSearchType,
              })
            }
          >
            <SearchDepartureCityDisplay
              fromCity={getDepartureCity()}
              subSearchType={subSearchType}
              departureAirport={
                !EVENTS_ENABLED && criteria.departureAirport
                  ? criteria.departureAirport
                  : firstOption
              }
              geolocation={geolocation}
              dominantSearchType={dominantSearchType}
            />
          </TouchableOpacity>
        )}
        {!hideArrival && (
          <TouchableOpacity
            style={styles.rowButton}
            onPress={() =>
              navigation.navigate(Modals.StationAutoCompleteModal, {
                returnScreen: route.name,
                source: 'to',
                index,
                hasAir,
                hasHotel,
                hasCar,
                hasRail,
                subSearchType,
              })
            }
          >
            <SearchArrivalCityDisplay
              toCity={arrivalToCity}
              subSearchType={subSearchType}
              arrivalAirport={criteria.arrivalAirport}
              dominantSearchType={dominantSearchType}
              geolocation={geolocation}
            />
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={styles.rowButton}
          onPress={() =>
            navigation.navigate(Modals.Calendar, {
              returnScreen: route.name,
              index,
              minDate:
                index === 0
                  ? null
                  : searchCriteria[index - 1].toDate
                  ? searchCriteria[index - 1].toDate
                  : searchCriteria[index - 1].fromDate,
              //allowRange: hasHotel || hasCar || subSearchType === 'roundtrip',
              allowRange: !(
                (dominantSearchType === 'air' || dominantSearchType === 'rail') &&
                ((subSearchType === 'oneway' && !(hasHotel || hasCar)) ||
                  subSearchType === 'multicity')
              ),
              showCarTimePickers: hasCar && !hasAir,
              isAirCar: hasCar && hasAir,
              isOneWay: subSearchType === subSearchTypeEnum.oneWay,
              fromDate: criteria.fromDate,
              toDate: criteria.toDate,
              dropOffTime: criteria.carDropOffTime,
              pickUpTime: criteria.carPickUpTime,
            })
          }
        >
          <SearchDateDisplay
            fromDate={criteria.fromDate}
            toDate={criteria.toDate}
            subSearchType={subSearchType}
            dominantSearchType={dominantSearchType}
            hasCar={hasCar}
            hasHotel={hasHotel}
          />
        </TouchableOpacity>
        {subSearchType !== 'multicity' && enablePassengerClass && (
          <TouchableOpacity
            onPress={() => {
              navigation.navigate(Modals.SearchTripOptionsModal, {
                returnScreen: route.name,
                currentAirClass: searchInputState.airClassType,
                hasAir,
                hasCar,
                hasHotel,
                tripOptions,
                travelersLimit,
              });
            }}
          >
            <SearchTripOptionsDisplay
              dominantSearchType={dominantSearchType}
              hasHotel={hasHotel}
              hasCar={hasCar}
              airClassType={airClassType}
              tripOptions={tripOptions}
            />
          </TouchableOpacity>
        )}
        {isBXservice(serviceType) ? (
          <View style={styles.textInputContainer}>
            <TextInput
              placeholder="Auth Number"
              style={[styles.customTextInput, CommonStyles.h2]}
              value={authNumber}
              onChangeText={(text) => {
                dispatch({ type: 'setAuthNumber', authNumber: text });
              }}
            />
          </View>
        ) : (
          <></>
        )}
      </View>
      {subSearchType === 'multicity' &&
        isLast &&
        enablePassengerClass &&
        searchCriteria.length < 6 && (
          <View style={styles.addCityButton}>
            <TouchableOpacity onPress={() => dispatch({ type: 'addCity' })}>
              <BaseText style={styles.addCityButtonText}> {t(tKey('AddCity'))}</BaseText>
            </TouchableOpacity>
          </View>
        )}
      {subSearchType === 'multicity' &&
        isLast &&
        searchCriteria.length <= 6 &&
        searchCriteria.length > 2 && (
          <View style={styles.addCityButton}>
            <TouchableOpacity onPress={() => dispatch({ type: 'removeCity' })}>
              <BaseText style={styles.addCityButtonText}> Remove City</BaseText>
            </TouchableOpacity>
          </View>
        )}
      {subSearchType === 'multicity' && enablePassengerClass && isLast && (
        <View style={styles.widgetContainer}>
          <TouchableOpacity
            onPress={() => {
              navigation.navigate(Modals.SearchTripOptionsModal, {
                returnScreen: route.name,
                currentAirClass: searchInputState.airClassType,
                hasAir,
                hasCar,
                hasHotel,
                tripOptions,
                travelersLimit,
              });
            }}
          >
            <SearchTripOptionsDisplay
              dominantSearchType={dominantSearchType}
              hasHotel={hasHotel}
              hasCar={hasCar}
              airClassType={airClassType}
              tripOptions={tripOptions}
            />
          </TouchableOpacity>
        </View>
      )}
    </>
  );
};

export const TripSearchForm = ({ dispatch, searchInputState }) => {
  const { searchCriteria } = searchInputState;
  return (
    <>
      {searchCriteria.map((criteria, index) => (
        <TripSearchFormFields
          key={index}
          index={index}
          showPicker={index === 0}
          criteria={criteria}
          dispatch={dispatch}
          searchInputState={searchInputState}
        />
      ))}
    </>
  );
};
const UnderText = ({ text }) => {
  return (
    <BaseText style={{ color: Color.grayText, ...CommonProperties.small }}>{`\n${text}`}</BaseText>
  );
};
const TripSearchWidget = (props) => {
  const { t } = useTranslation(i18nNS.searchWidget);
  const { tKey } = useComponentTKey('TripSearchWidget');
  //only for Events
  const tezEvent = useSelector(selectTezEvent);
  const tezEventPax = useSelector(selectTezEventPassengers);
  const reactDispatch = useDispatch();
  const eventGroupKey = useSelector(selectTezEventGroup);
  const tokenData = useSelector(selectTokenData);
  const formatDate = useDateFormat();
  //This block is used for AA states
  const initState = props.initState ? props.initState : undefined;
  const options = initState?.options ? initState?.options : undefined;
  const isLeisureAllowed = props.initState?.isLeisureAllowed;
  const selectedServiceType = initState?.serviceType
    ? initState.serviceType
    : isLeisureAllowed
    ? AAServiceTypes.LEISURE.text
    : AAServiceTypes.CORPORATE_TRAVEL.text;
  if (initState !== undefined) {
    initState.serviceType = selectedServiceType;
  }
  //The end of AA block
  const [
    { searchInputState, isNearbyHotelTonightSearch, handleSearchTypeChecked, handleSearchSubmit },
    dispatch,
  ] = useTripSearchWidget(props.allowRehydration, props.onSearch, initState);
  const { hasAir, hasHotel, hasCar, hasRail } = searchInputState;

  return (
    <View style={CommonStyles.px3}>
      {EVENTS_ENABLED && (
        <View style={styles.widgetContainer}>
          <View style={styles.rowButton}>
            <BaseText style={CommonStyles.ml2}>
              {tezEventPax[0]?.FirstName != undefined
                ? `${tezEventPax[0]?.FirstName} ${tezEventPax[0]?.LastName}`
                : 'Guest'}
            </BaseText>
            <View style={styles.messageBoxHeading}>
              <BaseText style={[CommonStyles.h2, CommonStyles.ml2]}>{tezEvent?.Name}</BaseText>
            </View>
            <BaseText style={CommonStyles.ml2}>
              {!tezEvent?.HideDatesInHeader
                ? `${formatDate(tezEvent?.FromDate)} to ${formatDate(tezEvent?.ToDate)}`
                : null}
            </BaseText>
          </View>
        </View>
      )}

      {AA_ENABLED && (
        <ActionSheetDropdown
          selectedOption={searchInputState.serviceType}
          onChangedOption={(serviceType) => {
            dispatch({ type: 'setServiceType', serviceType });
            reactDispatch(setIsBXCurrencyCulture(serviceType));
          }}
          options={options}
          containerStyle={styles.widgetContainer}
        />
      )}
      <View testID='TezTabsMain' style={styles.searchTypeRowWrap}>
        <View style={styles.searchTypeRow}>
          {((EVENTS_ENABLED && tezEvent?.IsFlightAllowed) || !EVENTS_ENABLED) && (
            <SearchTypeCheckbox
              selected={hasAir}
              iconName="flight"
              iconLabel={t(tKey('Air'))}
              onPress={() => handleSearchTypeChecked('air')}
            />
          )}
          {((EVENTS_ENABLED && tezEvent?.IsHotelAllowed) || !EVENTS_ENABLED) && (
            <SearchTypeCheckbox
              selected={hasHotel}
              iconName="hotel"
              iconLabel={t(tKey('Hotel'))}
              onPress={() => handleSearchTypeChecked('hotel')}
            />
          )}
          {((EVENTS_ENABLED && tezEvent?.IsCarAllowed) || !EVENTS_ENABLED) && (
            <SearchTypeCheckbox
              selected={hasCar}
              iconName="car"
              iconLabel={t(tKey('Car'))}
              onPress={() => handleSearchTypeChecked('car')}
            />
          )}
          {!EVENTS_ENABLED && !AA_ENABLED && (
            <SearchTypeCheckbox
              selected={hasRail}
              iconName="rail"
              iconLabel={t(tKey('Rail'))}
              onPress={() => {
                handleSearchTypeChecked('rail');
              }}
              style={styles.searchTypeLastChild}
            />
          )}
        </View>
      </View>
      <TripSearchForm dispatch={dispatch} searchInputState={searchInputState} />
      <View>
        <Button
          style={styles.submitButton}
          label={
            isNearbyHotelTonightSearch() ? t(tKey('SearchNearbyHotelsTonight')) : t('common:Search')
          }
          labelStyle={isNearbyHotelTonightSearch() ? styles.nearbyHotel : CommonStyles.h2}
          loading={props.loading}
          disabled={props.loading}
          onPress={handleSearchSubmit}
        />
      </View>

      <Messages
        companyKey={EVENTS_ENABLED ? tezEvent?.CompanyKey : tokenData?.companyKey}
        groupKey={EVENTS_ENABLED ? eventGroupKey : tokenData?.groupKey}
        pageKey={pageKeys.HOME}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  submitButton: {
    ...CommonProperties.py3,
    ...CommonProperties.shadow,
    ...CommonProperties.borderRadius4,
  },
  widgetContainer: {
    backgroundColor: Color.accent,
    ...CommonProperties.mt2,
    ...CommonProperties.mb3,
    ...CommonProperties.borderRadius,
    ...CommonProperties.shadow,
  },
  searchTypeRow: {
    height: scale(65),
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: Color.accent,
    overflow: 'hidden',
    ...CommonProperties.borderRadius,
  },
  searchTypeRowWrap: {
    ...CommonProperties.mt2,
    ...CommonProperties.mb3,
    ...CommonProperties.borderRadius,
    ...CommonProperties.shadow,
  },
  searchTypeLastChild: {
    borderRightWidth: 0,
  },
  rowButton: {
    borderBottomWidth: 0.7,
    borderColor: Color.shadow,
    ...CommonProperties.p3,
  },
  addCityButton: {
    ...CommonProperties.my1,
    ...CommonProperties.p1,
    ...CommonProperties.centerAlignment,
    flexDirection: 'row',
  },
  addCityButtonText: {
    color: Color.primary,
  },
  nearbyHotel: {
    ...CommonProperties.h4,
    ...CommonProperties.my2,
  },
  textInputContainer: {
    borderBottomWidth: 0.7,
    borderColor: Color.shadow,
    borderTopWidth: 0.6,
  },
  customTextInput: {
    height: scale(52),
    ...CommonProperties.fontFamily,
    ...CommonProperties.px2,
    ...CommonProperties.mx3,
    ...CommonProperties.borderRadius,
  },
  messageBoxHeading: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

export default TripSearchWidget;
